import React, { useEffect } from 'react';
import { Form, Formik, FormikProps } from 'formik';
import axios from 'axios';
import { useDispatch, useSelector } from 'react-redux';

import * as Store from 'store';
import * as StepperModule from 'modules/stepper';
import ScrollToErrorField from 'components/Fields/ScrollToError';
import { useProcess } from '../hooks';

import * as Actions from '../actions';
import * as Selectors from '../selectors';
import * as Types from '../types';
import { parseColumns } from 'helpers/parseColumns';
import { FIELD_TYPE } from '../constants';
import toast from 'react-hot-toast';
import { useTranslation } from 'react-i18next';

interface FormValues extends Types.IForm.Update {}

interface IChildren extends FormikProps<FormValues> {
  handleBack: () => void;
}

interface IProps {
  applicationId: number;
  stepId: number;
  position: number;
  className?: string;
  onSuccess?: ({ process }: { process: Types.IEntity.Process }) => void;
  onError?: (error: { message: string }) => void;
  onFinally?: () => void;

  children(props: IChildren): JSX.Element;
}

let cancelSource = axios.CancelToken.source();

const Update: React.FC<IProps> = ({
  applicationId,
  stepId,
  position,
  className,
  onSuccess = () => {},
  onError = () => {},
  onFinally = () => {},
  children
}) => {
  const dispatch = useDispatch();
  const { t } = useTranslation();
  const values = useSelector<Store.Types.IState, Types.IForm.Update>(state => Selectors.getValues(state, position));
  const { info } = useProcess();
  const { currentStep } = StepperModule.useContext();
  useEffect(() => () => cancelSource.cancel('canceled'), []);

  let formFn: FormikProps<FormValues>;
  const bindForm = form => {
    formFn = form;
  };

  function checkDataTableValues(values: FormValues) {
    const fieldIds = info.fields.byStep[currentStep.number!];
    const tableFields = fieldIds.map(id => info.fields.entities[id]).filter(f => f.type === FIELD_TYPE.DATA_TABLE);
    const hasErrors = tableFields.some(field => {
      const cols = parseColumns(field.columns);
      const rows = JSON.parse(values[field.key]?.value || '[]');
      return rows.some(row => cols.some(col => col.required && !row[col.field]));
    });

    return hasErrors;
  }

  const handleSubmit = (values: FormValues, { setSubmitting }: FormikProps<FormValues>) => {
    const hasError = checkDataTableValues(values);
    if (hasError) {
      toast.error(t('please_fill_all_required_fields').toString());
      setSubmitting(false);
      return;
    }

    cancelSource = axios.CancelToken.source();

    dispatch(
      Actions.Update.request({
        applicationId,
        stepId,
        position,
        values,
        cancelSource,
        callback: {
          onSuccess,
          onError,
          onFinally: () => {
            setSubmitting(false);
            onFinally();
          }
        }
      })
    );
  };

  const handleBack = () => {
    if (!position) {
      return;
    }

    if (formFn) {
      const { isSubmitting, setSubmitting } = formFn;

      if (!isSubmitting) {
        setSubmitting(true);
        cancelSource = axios.CancelToken.source();

        dispatch(
          Actions.Back.request({
            applicationId,
            stepId,
            position,
            changePosition: position - 1,
            cancelSource,
            callback: {
              onSuccess,
              onError,
              onFinally: () => {
                setSubmitting(false);
                onFinally();
              }
            }
          })
        );
      }
    }
  };

  return (
    <Formik onSubmit={handleSubmit} initialValues={{ ...values }} validateOnMount enableReinitialize>
      {(props: FormikProps<FormValues>) => {
        bindForm(props);
        return (
          <Form {...{ className }}>
            <ScrollToErrorField />
            {children({ ...props, handleBack })}
          </Form>
        );
      }}
    </Formik>
  );
};

export default Update;
